import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUsers, FiFileText } from 'react-icons/fi';
import { browseCustomers } from '../../utils/customerApi';
import { CustomerBrowseItem } from '../../types/customer';

const QNavCustomerOverview: React.FC = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<CustomerBrowseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const customerList = await browseCustomers();
      setCustomers(customerList);
    } catch (err) {
      setError('Failed to load customers');
      console.error('Error loading customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerClick = (customerId: string) => {
    navigate(`/dashboard/customers/${customerId}`);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-secondary-600">Loading customers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={loadCustomers} className="qnav-btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col space-y-4">
      {/* Header */}
      <div className="mt-6 mx-4 bg-brand-gradient text-white p-6 rounded-qnav shadow-brand-card">
        <h1 className="text-2xl font-display font-bold">Engagement Document Management (Adoption Engagements)</h1>
      </div>

      {/* Customer List */}
      <div className="flex-1 mx-4 bg-surface rounded-qnav shadow-brand-card border border-secondary-200">
        <div className="p-6">
          {customers.length === 0 ? (
            <div className="text-center py-12 text-secondary-500">
              <FiUsers size={48} className="mx-auto mb-4 text-secondary-300" />
              <p className="text-lg mb-2">No customers available</p>
              <p className="text-sm">Contact your administrator to get access to customers</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {customers.map((customer) => (
                <div
                  key={customer.customerId}
                  onClick={() => handleCustomerClick(customer.customerId)}
                  className="bg-white rounded-qnav border border-secondary-200 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-primary-600 hover:text-primary-800 transition-colors">
                      {customer.customerName || customer.name || customer.customerId}
                    </h3>
                  </div>

                  <div className="flex items-center text-sm text-secondary-600">
                    <FiFileText className="mr-2" size={16} />
                    <span>{customer.documentCount} documents</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QNavCustomerOverview;
